import { useRef, useEffect, useCallback } from 'react';
import {
  isOptionSelected,
  getLocationString,
  shouldShowLocation,
  getOptionIndex,
  getDisplayData,
  shouldShowDocId,
} from '../helpers';

export const useAttorneyAutocomplete = ({
  memberOptions = [],
  selectedMembers = [],
  onMembersChange,
  onSearchChange,
  showDisplayNamesOnly = false,
}) => {
  // Store memberOptions in a ref to access in renderOption
  const memberOptionsRef = useRef(memberOptions);
  
  useEffect(() => {
    memberOptionsRef.current = memberOptions;
  }, [memberOptions]);

  // Handle option change
  const handleChange = useCallback(
    (_, newValue) => {
      onMembersChange(newValue);
    },
    [onMembersChange]
  );

  // Handle input change
  const handleInputChange = useCallback(
    (_, newInputValue, reason) => {
      if (reason === 'input') {
        onSearchChange(newInputValue);
      }
    },
    [onSearchChange]
  );

  // Get option props for rendering
  const getOptionProps = useCallback(
    (option) => {
      const currentOptions = memberOptionsRef.current;
      const optionSelected = isOptionSelected(option, selectedMembers);
      const locationString = getLocationString(option);
      const showLocation = shouldShowLocation(locationString);
      const optionIndex = getOptionIndex(option, currentOptions);
      const isFirstOption = optionIndex === 0;
      const { displayName, displayRole } = getDisplayData(option);
      const showDocId = shouldShowDocId(option);

      return {
        option,
        isSelected: optionSelected,
        isFirstOption,
        displayName,
        displayRole,
        locationString,
        shouldShowDocId: showDocId,
        shouldShowLocation: showLocation,
        showDisplayNamesOnly,
      };
    },
    [selectedMembers, showDisplayNamesOnly]
  );

  // Popper props configuration
  const popperProps = {
    placement: 'bottom-start',
    disablePortal: false,
    modifiers: {
      preventOverflow: {
        enabled: true,
        boundariesElement: 'viewport',
      },
      flip: {
        enabled: false,
      },
    },
  };

  return {
    memberOptionsRef,
    handleChange,
    handleInputChange,
    getOptionProps,
    popperProps,
  };
};

